import { CSSProperties, useMemo } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { BiErrorCircle } from 'react-icons/bi';

import {
  Weather_Cloud,
  Weather_Gray,
  Weather_Rain,
  Weather_Snow,
  Weather_Sun,
} from '~/assets/icons';

import { WeatherCode } from './enums';

export type WeatherTemperatureUnits = 'metric' | 'imperial'; // 섭씨 | 화씨

export type GetWeatherIconOptions = {
  isLoading?: boolean;
  title?: string;
  style?: CSSProperties;
  sky?: string;
};

export interface WeatherType {
  code: WeatherCode;
  sky: string;
  skyCode?: string;
}

export interface UseWeatherProps {
  withIcon?: boolean;
}

export const useWeather = (props?: UseWeatherProps) => {
  const withIcon = props?.withIcon;
  const { t } = useTranslation('global');

  const weathers: WeatherType[] = [
    {
      code: WeatherCode.Sun,
      sky: '맑음',
      skyCode: 'SKY_001',
    },
    {
      code: WeatherCode.Cloud,
      sky: '구름많음',
      skyCode: 'SKY_003',
    },
    {
      code: WeatherCode.Gray,
      sky: '흐림',
      skyCode: 'SKY_007',
    },
    {
      code: WeatherCode.Rain,
      sky: '비',
      skyCode: 'SKY_004',
    },
    {
      code: WeatherCode.Snow,
      sky: '눈',
      skyCode: 'SKY_005',
    },
  ];

  /**
   * skyCode를 code로 변환합니다.
   * @param {string} skyCode
   */
  const getCodeBySkyCode = (skyCode: string) => {
    return weathers.find((e) => e.skyCode === skyCode)?.code;
  };

  /**
   * weatherCode에 맞는 날씨 아이콘을
   * 반환합니다.
   *
   * @param code
   * @param options
   */
  const getWeatherIcon = (code?: string, options?: GetWeatherIconOptions) => {
    const iconStyle = { width: 24, height: 24, ...options?.style };

    if (options?.isLoading) {
      return <Skeleton.Avatar active size="small" />;
    }

    const iconProps = {
      title: options?.title,
      style: iconStyle,
    };

    switch (code) {
      case 'cloud':
        return <Weather_Cloud {...iconProps} />;
      case 'gray':
        return <Weather_Gray {...iconProps} />;
      case 'rain':
        return <Weather_Rain {...iconProps} />;
      case 'snow':
        return <Weather_Snow {...iconProps} />;
      case 'sun':
        return <Weather_Sun {...iconProps} />;
      default:
        return <BiErrorCircle color="#aab6c3" {...iconProps} />;
    }
  };

  const getWeatherName = (code?: WeatherCode) => {
    switch (code) {
      case 'cloud':
        return t('global:weather_cloud');
      case 'gray':
        return t('global:weather_gray');
      case 'rain':
        return t('global:weather_rain');
      case 'snow':
        return t('global:weather_snow');
      case 'sun':
        return t('global:weather_sun');
      default:
        return t('global:weather_empty');
    }
  };

  const getTemperatureUnitText = (units?: WeatherTemperatureUnits) => {
    if (units === 'imperial') {
      return '°F';
    }
    return '°C';
  };

  const weatherOptions = useMemo(() => {
    const getLabel = (code: WeatherCode) => {
      if (!withIcon) {
        return getWeatherName(code);
      }
      return (
        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 8,
          }}
        >
          {getWeatherIcon(code, {
            style: {
              width: '1.25rem',
              height: '1.25rem',
            },
          })}
          <span>{getWeatherName(code)}</span>
        </Box>
      );
    };
    return weathers.map((item) => {
      return {
        value: item.code,
        label: getLabel(item.code),
        searchText: getWeatherName(item.code),
        skyCode: item.skyCode,
      };
    });
  }, [withIcon, t]);

  const generateDisplayWeather = (
    weather?: WeatherCode,
    temperature?: {
      low?: number;
      high?: number;
      units?: WeatherTemperatureUnits;
    },
  ) => {
    if (!weather) return t('global:weather_empty');
    if (!temperature) return getWeatherName(weather);
    return `${temperature.low}${getTemperatureUnitText(temperature.units)} / ${
      temperature.high
    }${getTemperatureUnitText(temperature.units)} ${getWeatherName(weather)}`;
  };

  /**
   * 섭씨를 화씨로,
   * 화씨를 섭씨로 변환합니다.
   *
   * @param value
   * @param TargetUnits
   * @returns
   */
  const convertTemperatureUnit = (value: number, TargetUnits: WeatherTemperatureUnits) => {
    /**
     * 섭씨를 화씨로 변환
     */
    if (TargetUnits === 'imperial') {
      return Math.floor(value * 1.8 + 32);
    }

    /**
     * 화씨를 섭씨로 변환
     */
    return Math.floor((value - 32) / 1.8);
  };

  return {
    weathers,
    weatherOptions,
    getCodeBySkyCode,
    getWeatherIcon,
    getWeatherName,
    getTemperatureUnitText,
    generateDisplayWeather,
    convertTemperatureUnit,
  };
};
